import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { FuseSplashScreenService } from "@barebone-fuse/@fuse/services/splash-screen.service";
import { CommandService } from "@core/services/command.service";
import { PlatformQueryService } from "@core/services/platform-query.service";
import { SnackBarService } from "@core/services/snack-bar.service";
import { PlatformToolSpreadsheetHandler } from "@features/spreadsheet/services/platform-tool-spreadsheet-handler";
import { SpreadsheetCommandFactory } from "@features/spreadsheet/spreadsheet-command";
import { SpreadsheetQueryFactory } from "@features/spreadsheet/spreadsheet-query";
import { ActivityFilterService } from "@features/task-manager/collaboration/services/activity-filter.service";
import {
  PlatformCommand,
  PlatformCommandResponse,
} from "@shared/commands/command-base";
import { IPlatformToolHandler } from "@shared/components/platform-tool/platform-tool-container/platform-tool-component";
import { Api } from "@shared/constants/common-field.constants";
import { EventStatus } from "@shared/models/notification-message.model";
import { MagicLinkModel } from "@shared/models/platform-tool-models";
import { DialogService } from "@shared/services/dialog-service";
import { Guid } from "guid-typescript";
import { Observable, Subject, of } from "rxjs";
import { catchError, map, take } from "rxjs/operators";
import {
  AdditionalCheckListItem,
  CheckStatus,
  ChecklistItem,
  JumpRunProgress,
  ScenarioCalculationStepModel,
} from "./step-by-step-process";

export interface StepByStepProcessContext {
  readonly platformLink: MagicLinkModel;
  readonly isAutomaticNavigation: boolean;
  readonly toolHandler: IPlatformToolHandler;
  readonly steps: ScenarioCalculationStepModel[];
}

@Injectable({ providedIn: "root" })
export class StepByStepProcessHandler {
  readonly stepsRefreshed$ = new Subject<{
    steps: ScenarioCalculationStepModel[];
    disableChecklist: boolean;
    isStepJumpEnabled: boolean;
    shouldFocus: boolean;
  }>();
  readonly focusRequested$ = new Subject<void>();
  readonly jumpRunResumed$ = new Subject<JumpRunProgress | null>();

  private readonly checklistItemUndoQuestion: string = `<div class="note">
            <strong style="display: block;font-size: 16px;margin-block-end: 20px">Are you sure you want to undo this checklist item?</strong>
                <p>By continuing, this checklist item and all <b>subsequent checklist items</b> for this step and all <b>subsequent steps</b> will be undone. You will need to complete these checklist items again before completing each step.</p>
            </div>`;
  private readonly stepCalculationItemUndoQuestion: string = `<div class="note">
            <strong style="display: block;font-size: 16px;margin-block-end: 20px">Are you sure you want to undo this step?</strong>
                <p>By continuing, this step and all <b>subsequent steps</b> will be undone. You will need to complete these step again.</p>
            </div>`;

  private buildJumpToStepNote(
    target: ScenarioCalculationStepModel,
    bypassed: ScenarioCalculationStepModel[],
    unrun: { step: ScenarioCalculationStepModel; name: string }[],
    willRunWorkflows: string[] = [],
    extraNote: string = "",
    actualTargetStep?: number,
  ): string {
    // When the server's PreviewOnly response caps the landing step short of what was clicked, only claim the steps actually reached.
    const isCapped = actualTargetStep != null && actualTargetStep < target.step;
    const displayBypassed = isCapped
      ? bypassed.filter((s) => s.step <= actualTargetStep)
      : bypassed;
    const displayTargetStep = isCapped ? actualTargetStep : target.step;

    const stepsList = displayBypassed.map((s) => `${s.step}. ${s.title}`).join("<br>");
    const stepsBlock = `<strong style="display: block;font-size: 16px;margin-block-end: 20px">The following steps will be marked complete:</strong>
                <p>${stepsList}<br><br>Step ${displayTargetStep} will become the in-progress step.</p>`;

    const willRunBlock = willRunWorkflows.length
      ? `<strong style="display: block;font-size: 16px;margin-block-start: 20px;margin-block-end: 20px">The following workflows will run, in order, before you land on the target step:</strong>
                <ul>${willRunWorkflows
                  .map((name) => `<li>${this.sanitizeHtml(name)}</li>`)
                  .join("")}</ul>
                <p>This can take several minutes. The checklist will be read-only until it finishes, and QNR subfield data will not be transferred.</p>`
      : "";

    // Filtered so nothing appears in both this list and the "will run" list above.
    const skippedUnrun = unrun.filter(
      (u) => !willRunWorkflows.includes(u.name) && (!isCapped || u.step.step <= actualTargetStep),
    );
    const unrunList = skippedUnrun.length
      ? `<strong style="display: block;font-size: 16px;margin-block-start: 20px;margin-block-end: 20px">These mandatory items will be skipped without running:</strong>
                <ul>${skippedUnrun
                  .map(
                    (u) =>
                      `<li>${this.sanitizeHtml(u.name)} (step ${u.step.step})</li>`,
                  )
                  .join("")}</ul>`
      : "";

    const cappedNote = isCapped
      ? `<strong style="display: block;font-size: 16px;margin-block-start: 20px;margin-block-end: 20px">This jump can only reach step ${actualTargetStep}:</strong>
                <p>Running ${this.sanitizeHtml(willRunWorkflows[0] ?? "")} stops the jump there. You'll need to jump again from step ${actualTargetStep + 1} to continue toward step ${target.step}.</p>`
      : "";

    return `<div class="note">
            ${stepsBlock}
            ${willRunBlock}
            ${cappedNote}
            ${unrunList}
            ${extraNote}
            </div>`;
  }

  private sanitizeHtml(value: string): string {
    const div = document.createElement("div");
    div.textContent = value ?? "";
    return div.innerHTML;
  }

  constructor(
    private platformQueryService: PlatformQueryService,
    private commandService: CommandService,
    private fuseSplashScreenService: FuseSplashScreenService,
    private dialogService: DialogService,
    private snackBarService: SnackBarService,
    private router: Router,
    private platformToolSpreadsheetHandler: PlatformToolSpreadsheetHandler,
    private activityFilterService: ActivityFilterService,
  ) {}

  onActivateCalculator(
    scenarioCalculationStep: ScenarioCalculationStepModel,
    ctx: StepByStepProcessContext,
  ): void {
    this.dialogService
      .showConfirmDialogWithNote(
        "Activate Calculator",
        "Continue",
        "Cancel",
        `By activating this calculator (${scenarioCalculationStep.title}), any existing progress made across subsequent calculators will be reset.`,
        "Are you sure you want to continue?",
      )
      .pipe(take(1))
      .subscribe((dialogResponse) => {
        if (dialogResponse.action === "OK") {
          const updateCommand =
            SpreadsheetCommandFactory.calculationStepMarkAsCompleteCommand(
              scenarioCalculationStep,
              ctx.platformLink,
            );

          this.fuseSplashScreenService.show();
          this.commandService
            .executePlatformCommand(updateCommand)
            .pipe(take(1))
            .subscribe(() => {
              ctx.toolHandler.invalidateSubject.invalidate("Refresh");
            });
        }
      });
  }

  executeCommand(
    command: PlatformCommand,
    ctx: StepByStepProcessContext,
  ): void {
    if (!command.name) {
      return;
    }
    this.fuseSplashScreenService.show();
    this.commandService
      .executePlatformCommand(command)
      .pipe(
        take(1),
        catchError((error) => {
          this.fuseSplashScreenService.hide();
          return of(new PlatformCommandResponse(error));
        }),
      )
      .subscribe((res) => {
        if (res.fieldValues["RedirectNewTab"]) {
          const url = res.fieldValues["RedirectNewTab"];
          window.open(url, "_blank");
        }
        if (res.status === EventStatus.Failed) {
          this.snackBarService.showError(res.fieldValues["SnackBarMessage"]);
        }
        if (
          res.status !== EventStatus.Failed &&
          res.fieldValues["SnackBarMessage"]
        ) {
          const duration =
            res.fieldValues["SnackBarMessageDuration"] ??
            res.fieldValues["SnackBarMessage"].length * 80;
          this.snackBarService.showInformation(
            res.fieldValues["SnackBarMessage"],
            duration,
          );
        }
        if (res.fieldValues["RedirectUrl"]) {
          this.router.navigateByUrl(res.fieldValues["RedirectUrl"]);
          this.fuseSplashScreenService.hide();
          return;
        }
        if (res.fieldValues["RedirectCommand"]) {
          const redirectCommand = res.fieldValues[
            "RedirectCommand"
          ] as PlatformCommand;
          this.executeCommand(redirectCommand, ctx);
          return;
        }
        if (res.fieldValues["Refetch"]) {
          this.refetchSteps(
            ctx,
            true,
            command.fieldValues["ChecklistNavigationEnabled"],
          );
        }

        if (res.name === "UpdatePlatformLinkModelCommand") {
          this.focusRequested$.next();
        }

        this.fuseSplashScreenService.hide();
      });
  }

  refetchSteps(
    ctx: StepByStepProcessContext,
    shouldFocus: boolean = false,
    checklistNavigationEnabled: boolean = false,
  ): void {
    const getScenarioChecklistsQuery = SpreadsheetQueryFactory.fetchStepsQuery(
      ctx.platformLink,
    );
    this.fuseSplashScreenService.show();
    this.platformQueryService
      .executeQuery(getScenarioChecklistsQuery)
      .pipe(take(1))
      .subscribe((res) => {
        const newSteps: ScenarioCalculationStepModel[] = res.fieldValues[
          "ScenarioCalculationStep"
        ]
          ? res.fieldValues["ScenarioCalculationStep"]
          : [];
        const newDisableChecklist: boolean =
          res.fieldValues["DisableChecklist"] ?? false;
        const newIsStepJumpEnabled: boolean =
          res.fieldValues["IsStepJumpEnabled"] ?? false;
        this.stepsRefreshed$.next({
          steps: newSteps,
          disableChecklist: newDisableChecklist,
          isStepJumpEnabled: newIsStepJumpEnabled,
          shouldFocus,
        });
        this.fuseSplashScreenService.hide();
        let navigationSucceed = false;
        if (checklistNavigationEnabled) {
          navigationSucceed = this.navigateToChecklist(ctx);
        }
        if (
          !navigationSucceed ||
          (!ctx.isAutomaticNavigation &&
            ctx.platformLink.fieldValues["IsOpenCalculator"] === true)
        ) {
          ctx.toolHandler.invalidateSubject.invalidate(
            "HardReload" + Guid.create().toString(),
          );
        }
      });
    this.activityFilterService.shouldFetchActivityFeeds(true);
  }

  // Catches a remounted sidebar up on a jump run it may have missed the live push for (e.g. Centrifugo subscription torn down and recreated).
  reconcileJumpRunOnMount(ctx: StepByStepProcessContext): void {
    const getScenarioChecklistsQuery = SpreadsheetQueryFactory.fetchStepsQuery(
      ctx.platformLink,
    );
    this.platformQueryService
      .executeQuery(getScenarioChecklistsQuery)
      .pipe(take(1))
      .subscribe((res) => {
        const jumpRun = res.fieldValues["JumpRun"] as JumpRunProgress | null;
        if (!jumpRun) {
          this.jumpRunResumed$.next(null);
          return;
        }
        if (jumpRun.status === "Running") {
          this.jumpRunResumed$.next(jumpRun);
          return;
        }
        // Completed or Failed while this component wasn't actively subscribed -- catch up now.
        this.jumpRunResumed$.next(null);
        this.onJumpRunFinished(jumpRun, ctx);
      });
  }

  private navigateToChecklist(
    ctx: StepByStepProcessContext,
    navigateFirst: boolean = false,
  ): boolean {
    const activeScenarioCalculationStep = ctx.steps.find(
      (step) => step.status === CheckStatus.InProgress,
    );
    let firstNavigationItem;
    if (navigateFirst) {
      firstNavigationItem = activeScenarioCalculationStep.checklists.find(
        (o) =>
          o.isNavigationCommand &&
          !o.isChecked &&
          o.status === CheckStatus.InProgress,
      );
    } else {
      firstNavigationItem = activeScenarioCalculationStep.checklists.find(
        (o) => o.isNavigationCommand && !o.isChecked,
      );
    }
    const firstNavigationIndex =
      activeScenarioCalculationStep.checklists.indexOf(firstNavigationItem);
    if (firstNavigationIndex >= 0 && ctx.isAutomaticNavigation) {
      this.onHyperlinkClick(
        activeScenarioCalculationStep,
        firstNavigationItem,
        firstNavigationIndex,
        ctx,
      );
    }
    return firstNavigationIndex >= 0;
  }

  onMarkAsComplete(
    scenarioCalculationStep: ScenarioCalculationStepModel,
    ctx: StepByStepProcessContext,
    checklistIndex?: number,
  ): void {
    const currentUrl = this.router.url;
    const updateCommand =
      SpreadsheetCommandFactory.calculationStepMarkAsCompleteCommandV2(
        scenarioCalculationStep,
        ctx.platformLink,
        true,
        checklistIndex,
        currentUrl,
      );

    this.fuseSplashScreenService.show();
    updateCommand.fieldValues["ChecklistNavigationEnabled"] = true;
    this.executeCommand(updateCommand, ctx);
  }

  onExecuteOtherWorkflow(
    scenarioCalculationStep: ScenarioCalculationStepModel,
    checklistIndex: number,
    additionalCheckListItem: AdditionalCheckListItem | null,
    ctx: StepByStepProcessContext,
  ): void {
    const currentUrl = this.router.url;
    const workflowName =
      additionalCheckListItem != null
        ? additionalCheckListItem.workflowName
        : scenarioCalculationStep.checklists[checklistIndex].workflowName;
    const additionalParameters =
      additionalCheckListItem == null
        ? null
        : additionalCheckListItem.commandParameters;
    if (!scenarioCalculationStep.checklists[checklistIndex].isMandatory) {
      const message = 'Do you want to run "' + workflowName + '"?';
      this.dialogService
        .showConfirmDialog("Workflow Confirmation", "Ok", "Cancel", message, "")
        .subscribe((res) => {
          if (res.action === "OK") {
            const updateCommand =
              SpreadsheetCommandFactory.calculationStepMarkAsCompleteCommandV2(
                scenarioCalculationStep,
                ctx.platformLink,
                false,
                checklistIndex,
                currentUrl,
                additionalParameters,
              );

            this.fuseSplashScreenService.show();
            this.executeCommand(updateCommand, ctx);
          }
        });
    } else {
      const updateCommand =
        SpreadsheetCommandFactory.calculationStepMarkAsCompleteCommandV2(
          scenarioCalculationStep,
          ctx.platformLink,
          false,
          checklistIndex,
          currentUrl,
          additionalParameters,
        );

      this.fuseSplashScreenService.show();
      this.executeCommand(updateCommand, ctx);
    }
    this.trackRunWorkflowActivity(scenarioCalculationStep, workflowName, ctx);
  }

  onHyperlinkClick(
    scenarioCalculationStep: ScenarioCalculationStepModel,
    checklist: ChecklistItem,
    checklistIndex: number,
    ctx: StepByStepProcessContext,
  ): void {
    const currentUrl = this.router.url;
    if (checklist.commandName?.startsWith("NavigateToWorksheet")) {
      if (!checklist.commandParameters["IsWorksheetVisible"]) {
        this.snackBarService.showWarning(
          "We do not find this worksheet " +
            checklist.navigationCommand.fieldValues["LoadWorksheetName"] +
            " in this visual area",
        );
        return;
      }
      if (
        currentUrl.startsWith("/gmt/scenarios/open-calculator/") ||
        currentUrl.startsWith("/txp/scenarios/open-calculator/") ||
        (checklist?.commandParameters &&
          checklist?.commandParameters["IsOpenFilter"])
      ) {
        const command = new PlatformCommand(
          "SpreadsheetNavigationCommand",
          Api.Spreadsheet,
        );
        command.fieldValues["LoadWorksheet"] =
          checklist.navigationCommand.fieldValues["LoadWorksheet"];
        command.fieldValues["LoadWorkbook"] =
          checklist.navigationCommand.fieldValues["LoadWorkbook"];
        this.platformToolSpreadsheetHandler.executeCommand(command);
        const magicLinkUpdateCommand =
          ctx.platformLink.additionalCommands[
            "UpdatePlatformLinkModelCommand"
          ];
        if (magicLinkUpdateCommand) {
          magicLinkUpdateCommand.fieldValues["LoadWorkbook"] =
            checklist.navigationCommand.fieldValues["LoadWorkbook"];
          magicLinkUpdateCommand.fieldValues["LoadWorksheet"] =
            checklist.navigationCommand.fieldValues["LoadWorksheet"];
          magicLinkUpdateCommand.fieldValues["PlatformLinkModelId"] =
            ctx.platformLink.id;
          this.executeCommand(magicLinkUpdateCommand, ctx);
        }
      } else {
        if (checklist.navigationCommand) {
          this.executeCommand(checklist.navigationCommand, ctx);
        } else {
          this.snackBarService.showWarning("Navigation Command Not Found!");
        }
      }
    } else if (
      checklist.commandName?.startsWith("NavigateToDirectory") ||
      checklist.commandName?.startsWith("NavigateToGrid") ||
      checklist.commandName?.startsWith("ExportDataTemplate") ||
      checklist.commandName?.startsWith("NavigateToScenarioIconViewCommand")
    ) {
      if (checklist.navigationCommand) {
        this.executeCommand(checklist.navigationCommand, ctx);
      } else {
        this.snackBarService.showWarning("Navigation Command Not Found!");
      }
    } else if (checklist.commandName?.endsWith("PipelineCommand")) {
      if (checklist.navigationCommand) {
        const title = scenarioCalculationStep.checklists[checklistIndex].title
          .split(" ")
          .slice(1)
          .join(" ");
        const message = 'Do you want to run "' + title + '"?';
        this.dialogService
          .showConfirmDialog(
            "Workflow Confirmation",
            "Ok",
            "Cancel",
            message,
            "",
          )
          .subscribe((res) => {
            if (res.action === "OK") {
              this.fuseSplashScreenService.show();
              this.executeCommand(checklist.navigationCommand, ctx);
              this.trackRunWorkflowActivity(
                scenarioCalculationStep,
                title,
                ctx,
              );
            }
          });
      } else {
        this.snackBarService.showWarning("Navigation Command Not Found!");
      }
    } else if (checklist.commandParameters["CommandExecutionApi"]) {
      let command = new PlatformCommand(
        checklist.commandName,
        checklist.commandParameters["CommandExecutionApi"] ?? Api.Spreadsheet,
      );
      command.fieldValues["RedirectUrl"] = currentUrl;
      command.fieldValues["ScenarioId"] = scenarioCalculationStep.scenarioId;
      command.fieldValues["ScenarioType"] =
        scenarioCalculationStep.scenarioType;
      command.fieldValues["CountryCode"] = scenarioCalculationStep.countryCode;
      command.fieldValues["ScenarioCalculationId"] =
        scenarioCalculationStep.scenarioCalculationId;
      this.executeCommand(command, ctx);
    } else if (
      checklist.commandName?.endsWith("Command") &&
      !checklist.hasWorkflow
    ) {
      const updateCommand =
        SpreadsheetCommandFactory.calculationStepMarkAsCompleteCommandV2(
          scenarioCalculationStep,
          ctx.platformLink,
          false,
          checklistIndex,
          currentUrl,
        );
      this.fuseSplashScreenService.show();
      this.executeCommand(updateCommand, ctx);
    }
  }

  onDescriptionHyperlinkClick(
    event: MouseEvent,
    checklist: ChecklistItem,
  ): void {
    const target = event.target as HTMLElement;
    if (target.tagName === "A") {
      const command = checklist.descriptionCommands[target.textContent];
      if (command?.name) {
        this.platformToolSpreadsheetHandler.executeCommand(command);
      }
      console.log("Description link clicked from step-by-step");
      event.preventDefault();
    }
  }

  onAllChecking(
    $event: any,
    scenarioCalculationStep: ScenarioCalculationStepModel,
    ctx: StepByStepProcessContext,
  ): void {
    const isAllChecked: boolean = $event.checked;
    if (scenarioCalculationStep.status === "In Progress") {
      if (isAllChecked) {
        const checklistIndex = scenarioCalculationStep.checklists.length - 1;
        const checklistItem = JSON.parse(
          JSON.stringify(scenarioCalculationStep.checklists[checklistIndex]),
        );
        checklistItem.isChecked = true;
        this.updateScenarioCalculationStepChecklist(
          scenarioCalculationStep,
          checklistItem,
          ctx,
          true,
          isAllChecked,
        );
      } else {
        const message = null;
        this.dialogService
          .showConfirmDialog(
            "Undo Step",
            "Continue",
            "Cancel",
            message,
            this.stepCalculationItemUndoQuestion,
            true,
          )
          .subscribe((res) => {
            if (res.action === "OK") {
              const checklistItem = JSON.parse(
                JSON.stringify(scenarioCalculationStep.checklists[0]),
              );
              checklistItem.isChecked = false;
              this.updateScenarioCalculationStepChecklist(
                scenarioCalculationStep,
                checklistItem,
                ctx,
                true,
                isAllChecked,
              );
            } else {
              scenarioCalculationStep.checklists[0].isChecked = true;
              scenarioCalculationStep.isAllChecked = true;
            }
          });
      }
    } else if (scenarioCalculationStep.status === "Completed") {
      if (!isAllChecked) {
        const message = null;
        this.dialogService
          .showConfirmDialog(
            "Undo Step",
            "Continue",
            "Cancel",
            message,
            this.stepCalculationItemUndoQuestion,
            true,
          )
          .subscribe((res) => {
            if (res.action === "OK") {
              this.onMarkAsComplete(scenarioCalculationStep, ctx, 0);
            } else {
              scenarioCalculationStep.checklists[0].isChecked = true;
              scenarioCalculationStep.isAllChecked = true;
            }
          });
      }
    } else if (scenarioCalculationStep.status === CheckStatus.Pending) {
      this.onJumpToStep(scenarioCalculationStep, ctx);
    }
  }

  private onJumpToStep(
    step: ScenarioCalculationStepModel,
    ctx: StepByStepProcessContext,
  ): void {
    const current = ctx.steps.find(
      (s) => s.status === CheckStatus.InProgress,
    );
    if (!current || step.step <= current.step) {
      this.snackBarService.showWarning(
        "This step is no longer available. Please reload the checklist.",
      );
      step.isAllChecked = false;
      return;
    }

    const bypassed = [
      current,
      ...ctx.steps.filter((s) => s.step > current.step && s.step < step.step),
    ];
    const unrun = bypassed.flatMap((s) =>
      (s.checklists ?? [])
        .filter((c) => c.isMandatory && !c.isChecked)
        .map((c) => ({
          step: s,
          name: c.hasWorkflow ? c.workflowName : c.title,
        })),
    );
    const currentUrl = this.router.url;

    // The PreviewOnly round-trip before the confirm dialog can take several seconds -- show a wait
    // screen so the click doesn't look like it did nothing while the server responds.
    this.fuseSplashScreenService.show();

    this.previewWillRunWorkflows(step, ctx, currentUrl).subscribe(
      ({ workflowsToRun, actualTargetStep }) => {
        this.fuseSplashScreenService.hide();
        const htmlNote = this.buildJumpToStepNote(
          step,
          bypassed,
          unrun,
          workflowsToRun,
          "",
          actualTargetStep,
        );

        this.dialogService
          .showConfirmDialog(
            "Jump to Step",
            "Continue",
            "Cancel",
            null,
            htmlNote,
            true,
          )
          .pipe(take(1))
          .subscribe((res) => {
            if (res.action === "OK") {
              step.isAllChecked = false;
              const command =
                SpreadsheetCommandFactory.stepByStepJumpToStepCommand(
                  step,
                  ctx.platformLink,
                  currentUrl,
                );
              // Only auto-navigate to the next checklist item when no workflow will run -- otherwise it races the bypassed workflow's own redirect and can win.
              command.fieldValues["ChecklistNavigationEnabled"] = workflowsToRun.length === 0;
              this.executeCommand(command, ctx);
            } else {
              step.isAllChecked = false;
            }
          });
      },
    );
  }

  // Asks the server (PreviewOnly, no mutation) which workflows this exact jump will run, rather than guessing from the checklist's own flags.
  private previewWillRunWorkflows(
    step: ScenarioCalculationStepModel,
    ctx: StepByStepProcessContext,
    currentUrl: string,
  ): Observable<{ workflowsToRun: string[]; actualTargetStep?: number }> {
    const previewCommand = SpreadsheetCommandFactory.stepByStepJumpToStepCommand(
      step,
      ctx.platformLink,
      currentUrl,
      true,
    );
    return this.commandService.executePlatformCommand(previewCommand).pipe(
      take(1),
      map((res) => ({
        workflowsToRun: (res.fieldValues["WorkflowsToRun"] as string[]) ?? [],
        actualTargetStep: res.fieldValues["ActualTargetStep"] as number | undefined,
      })),
      catchError(() => of({ workflowsToRun: [] as string[] })),
    );
  }

  onJumpRunFinished(
    jumpRun: JumpRunProgress,
    ctx: StepByStepProcessContext,
  ): void {
    if (jumpRun.status === "Failed") {
      const ranNote = (jumpRun.ranWorkflowNames ?? []).length
        ? `<p>${jumpRun.ranWorkflowNames
            .map((name) => this.sanitizeHtml(name))
            .join(", ")} <strong>did</strong> run, and its changes to the country calculators have <strong>not been undone</strong>. Review that data before retrying.</p>`
        : "";
      const htmlNote = `<div class="note">
            <strong style="display: block;font-size: 16px;margin-block-end: 20px">"${this.sanitizeHtml(jumpRun.currentWorkflowName ?? "")}" did not complete.</strong>
            <p>You are on step ${jumpRun.currentStep}. The jump did not continue to step ${jumpRun.targetStep}.</p>
            ${ranNote}
            </div>`;
      this.dialogService
        .showDialogMessage("Step-by-Step Jump Failed", htmlNote)
        .pipe(take(1))
        .subscribe();
    }
    this.refetchSteps(ctx);
  }

  onCheck(
    checked: any,
    scenarioCalculationStep: ScenarioCalculationStepModel,
    checklist: ChecklistItem,
    ctx: StepByStepProcessContext,
  ): void {
    const checklistIndex = scenarioCalculationStep.checklists.findIndex(
      (s) => s.id === checklist.id,
    );

    if (scenarioCalculationStep.status === "In Progress") {
      if (checked) {
        this.updateScenarioCalculationStepChecklist(
          scenarioCalculationStep,
          checklist,
          ctx,
        );
      } else {
        const message = null;
        this.dialogService
          .showConfirmDialog(
            "Undo Checklist Item",
            "Continue",
            "Cancel",
            message,
            this.checklistItemUndoQuestion,
            true,
          )
          .subscribe((res) => {
            if (res.action === "OK") {
              this.updateScenarioCalculationStepChecklist(
                scenarioCalculationStep,
                checklist,
                ctx,
                true,
              );
            } else {
              scenarioCalculationStep.checklists[checklistIndex].isChecked =
                true;
            }
          });
      }
    } else if (scenarioCalculationStep.status === "Completed") {
      if (checked === false) {
        const message = null;
        this.dialogService
          .showConfirmDialog(
            "Undo Checklist Item",
            "Continue",
            "Cancel",
            message,
            this.checklistItemUndoQuestion,
            true,
          )
          .subscribe((res) => {
            if (res.action === "OK") {
              this.onMarkAsComplete(
                scenarioCalculationStep,
                ctx,
                checklistIndex,
              );
            } else {
              scenarioCalculationStep.checklists[checklistIndex].isChecked =
                true;
            }
          });
      }
    }
  }

  updateScenarioCalculationStepChecklist(
    scenarioCalculationStep: ScenarioCalculationStepModel,
    checklist: ChecklistItem,
    ctx: StepByStepProcessContext,
    shouldFocus: boolean = false,
    isAllChecked: boolean = false,
  ): void {
    const updateCommand =
      SpreadsheetCommandFactory.updateScenarioCalculationStepChecklistCommand(
        scenarioCalculationStep,
        checklist,
        ctx.platformLink,
        isAllChecked,
      );
    if (isAllChecked) {
      updateCommand.fieldValues["UrlAfterCompleteCheckMark"] = this.router.url;
    }
    this.fuseSplashScreenService.show();
    this.commandService
      .executePlatformCommand(updateCommand)
      .pipe(
        take(1),
        catchError((error) => {
          this.fuseSplashScreenService.hide();
          return of(new PlatformCommandResponse(error));
        }),
      )
      .subscribe((res) => {
        if (res.fieldValues["RedirectCommand"]) {
          const redirectCommand = res.fieldValues[
            "RedirectCommand"
          ] as PlatformCommand;
          this.executeCommand(redirectCommand, ctx);
          return;
        } else if (res.fieldValues["RedirectUrl"]) {
          this.router.navigateByUrl(res.fieldValues["RedirectUrl"]);
          this.fuseSplashScreenService.hide();
          return;
        } else if (res.fieldValues["SnackBarMessage"]) {
          this.snackBarService.showError(res.fieldValues["SnackBarMessage"]);
          if (isAllChecked) {
            const activeScenarioCalculationStep: ScenarioCalculationStepModel =
              ctx.steps.find((step) => step.id === scenarioCalculationStep.id);
            activeScenarioCalculationStep.isAllChecked = false;
          }
          return;
        }
        const scenarioCalculationStepModel = res.fieldValues[
          "ScenarioCalculationStepModel"
        ] as ScenarioCalculationStepModel;
        const index = ctx.steps.findIndex(
          (i) => i.id === scenarioCalculationStepModel.id,
        );
        if (index >= 0) {
          ctx.steps[index] = scenarioCalculationStepModel;
        }
        if (shouldFocus) {
          this.focusRequested$.next();
        }
        this.navigateToChecklist(ctx, true);
        this.fuseSplashScreenService.hide();
      });
  }

  private trackRunWorkflowActivity(
    scenarioCalculationStep: ScenarioCalculationStepModel,
    workflowName: string,
    ctx: StepByStepProcessContext,
  ): void {
    console.log(scenarioCalculationStep);
    const trackRunWorkflowActivityCommand = new PlatformCommand(
      "TrackRunWorkflowOnStepByStepActivityCommand",
      Api.Spreadsheet,
    );
    trackRunWorkflowActivityCommand.fieldValues["ScenarioCalculationStepId"] =
      scenarioCalculationStep.id;
    trackRunWorkflowActivityCommand.fieldValues["workflowName"] = workflowName;
    trackRunWorkflowActivityCommand.fieldValues["workbookSetId"] =
      ctx.platformLink.fieldValues["CalculatorEventSourceId"];
    this.executeCommand(trackRunWorkflowActivityCommand, ctx);
  }
}
